'use client'

import {
  createContext,
  type ReactNode,
  type TransitionStartFunction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'

import { ProgressBar } from '@/components/layout/ProgressBar'
import { type ScrollType } from '@/defs/scroll'
import { useReduxDispatch } from '@/store/hooks'
import { modalActions } from '@/store/modal'
import { viewActions } from '@/store/view'
import { scrollToTarget } from '@/utils/scrollUtil'

type ScrollRequestType = {
  scroll?: ScrollType
  scrollY: number
}

type NavigationActionsType = {
  consumeScrollRequest: () => ScrollRequestType | null
  recordPendingNavigation: () => () => void
  setScrollRequest: (href: string, scroll?: ScrollType) => void
  startRedirect: TransitionStartFunction
}

const NavigationActionsContext = createContext<NavigationActionsType | null>(null)
const NavigationIsNavigatingContext = createContext<boolean | null>(null)

const getUrlKey = ({ pathname, search }: URL | Location) => `${pathname}${search}`

type Props = {
  children: ReactNode
}

export const NavigationProvider = ({ children }: Props) => {
  const dispatch = useReduxDispatch()
  const [pendingCount, setPendingCount] = useState(0)
  const [isRedirecting, startRedirect] = useTransition()
  const scrollRequestRef = useRef<ScrollRequestType | null>(null)

  const recordPendingNavigation = useCallback(() => {
    setPendingCount((count) => count + 1)

    return () => setPendingCount((count) => count - 1)
  }, [])

  const setScrollRequest = useCallback(
    (href: string, scroll?: ScrollType) => {
      const url = new URL(href, window.location.href)

      // If the current route is the same, close menus and scrollto
      if (getUrlKey(url) === getUrlKey(window.location)) {
        scrollRequestRef.current = null
        dispatch(viewActions.closeMobileMenus())
        dispatch(modalActions.closeAll())
        requestAnimationFrame(() => scrollToTarget({ hash: url.hash.slice(1), scroll }))
        return
      }

      scrollRequestRef.current = { scroll, scrollY: window.scrollY }
    },
    [dispatch],
  )

  const consumeScrollRequest = useCallback(() => {
    const scrollRequest = scrollRequestRef.current
    scrollRequestRef.current = null

    return scrollRequest
  }, [])

  const actions = useMemo(
    () => ({ consumeScrollRequest, recordPendingNavigation, setScrollRequest, startRedirect }),
    [setScrollRequest, recordPendingNavigation, consumeScrollRequest, startRedirect],
  )

  return (
    <NavigationActionsContext.Provider value={actions}>
      <NavigationIsNavigatingContext.Provider value={isRedirecting || pendingCount > 0}>
        {children}
        <ProgressBar />
      </NavigationIsNavigatingContext.Provider>
    </NavigationActionsContext.Provider>
  )
}

export const useNavigationActions = (): NavigationActionsType => {
  const actions = useContext(NavigationActionsContext)

  if (!actions) throw new Error('useNavigationActions must be used within NavigationProvider')

  return actions
}

export const useIsNavigating = (): boolean => {
  const isNavigating = useContext(NavigationIsNavigatingContext)

  if (isNavigating === null) throw new Error('useIsNavigating must be used within NavigationProvider')

  return isNavigating
}
